// hero-8.twig
import React from 'react'
import styled, {css} from 'styled-components'

import {
  contextualSpaces,
  workshops,
  ButtonAdapter,
  Content,
  Headline,
  LazyImageAdapter,
} from '@miro-site/components_utils'
import {Section, Video, Box, useWindowScroll} from '@miro-site/core'

import type {Assets} from '@miro-site/components_utils'

import type {StyleProps, BoxProps} from '@miro-site/core'

const {WORKSHOPS_BG_COLORS} = workshops

type BackgroundType = keyof typeof WORKSHOPS_BG_COLORS
type HeadlineType = React.ComponentProps<typeof Headline>

interface HeroFirstLevelProps {
  background?: BackgroundType
  headline?: HeadlineType
  button?: React.ComponentProps<typeof ButtonAdapter>
  content?: React.ComponentProps<typeof Content>
  shape?: Assets
}

type ImageAndVideoProps =
  | {
      image: Assets
      video?: never
    }
  | {
      video: React.ComponentProps<typeof Video>
      image?: never
    }

const SECTION_STYLES: StyleProps = {
  position: 'relative',
  overflow: 'hidden',
  height: ['auto', 1000],
  marginBottom: [48, 88],
}

const Row = styled(Box).attrs({
  $my: [null, 0],
  $mx: [null, -10],
})`
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;

  .hero-button {
    ${({theme}) => css`
      ${theme.mq.small(css`
        margin-top: ${theme.spaces.lg}px;
      `)}
    `}
  }
`

interface ColProps extends BoxProps {
  $isSecond?: boolean
}

const Col = styled(Box).attrs(({$isSecond}: ColProps) => {
  const boxProps: BoxProps = {
    $py: [null, 0],
    $px: [null, 10],
    $styles: {width: ['100%', '50%']},
  }
  if ($isSecond) {
    boxProps.$pt = [64, 12]
  }
  return boxProps
})<ColProps>`
  flex: 0 0 50%;

  ${({theme}) => css`
    ${theme.mq.small(css`
      flex: 0 0 100%;
    `)}
  `}
`

const ImagesSection = styled(Box).attrs({
  $mt: [40, 80],
  $py: [39, null, null, null],
  $px: [20, null, null, null],
  $styles: {
    margin: '0 auto',
    maxWidth: [null, 1430],
  },
})`
  position: relative;
  overflow: hidden;
`

interface ImagesContainerProps extends BoxProps {
  $setOffsetTop?: boolean
}

const ImagesContainer = styled(Box).attrs({
  $px: [null, 0],
  $pt: [null, 120],
  $pb: [null, 80],
})<ImagesContainerProps>`
  ${({$setOffsetTop}) =>
    $setOffsetTop &&
    css`
      padding-top: 0;
    `}
`

const Shape = styled(Box).attrs({
  $styles: {
    width: [365, 1430],
  },
})`
  height: auto;
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
`

const ImageContainer = styled(Box).attrs({
  $styles: {
    width: ['100%', 1120],
    borderRadius: [4, 8],
  },
})`
  display: block;
  height: auto;
  margin: 0 auto;
  position: relative;
  z-index: 3;
  overflow: hidden;

  ${({theme}) => css`
    ${theme.mq.small(css`
      box-shadow: 0 ${theme.multipliers.small(8)} ${theme.multipliers.small(24)}
        rgba(5, 0, 56, 0.12);
    `)}
    ${theme.mq.mediumDown(css`
      box-shadow: 0 ${theme.multipliers.mediumDown(8)} ${theme.multipliers.mediumDown(24)}
        rgba(5, 0, 56, 0.12);
    `)}
    ${theme.mq.mediumUp(css`
      box-shadow: 0 ${theme.multipliers.mediumUp(8)} ${theme.multipliers.mediumUp(24)}
        rgba(5, 0, 56, 0.12);
    `)}
    ${theme.mq.large(css`
      box-shadow: 0 ${theme.multipliers.large(8)} ${theme.multipliers.large(24)}
        rgba(5, 0, 56, 0.12);
    `)}
  `};
`

const VideoContainer = styled(Box).attrs({
  $styles: {
    borderRadius: [2, 8],
    width: [null, 1120],
  },
})`
  margin: 0 auto;
  position: relative;
  z-index: 3;
  overflow: hidden;
`

const getHeadline = (headlineProps: HeadlineType, background: BackgroundType = '') => {
  Object.assign(headlineProps, {
    size: headlineProps.size || 'medium',
    tag: headlineProps.tag || 'h1',
    color: workshops.isHighContrastColor(background) ? 'white' : headlineProps.color,
    align: headlineProps.align || 'left',
  })

  return <Headline {...headlineProps}></Headline>
}

const ScrollContainer = styled.div`
  height: 100%;
  overflow: hidden;
`

const DEFAULT_SHAPE_SIZES = {
  desktop: {
    width: 1430,
    height: 845,
  },
  mobile: {
    width: 365,
    height: 340,
  },
}

const HeroFirstLevel: React.FC<HeroFirstLevelProps & ImageAndVideoProps> = ({
  headline,
  background = 'workshops-color-grey',
  button,
  content,
  shape,
  image,
  video,
}) => {
  const ref = React.useRef<HTMLDivElement>(null)
  const textColor = workshops.isHighContrastColor(background)
    ? 'white'
    : background == ''
    ? 'dark'
    : null

  useWindowScroll(() => {
    if (!ref.current) {
      return
    }
    ref.current.scrollTop = window.pageYOffset
  })

  return (
    <Section
      data-place="Hero Section"
      $styles={SECTION_STYLES}
      $noPaddingTop={true}
      $noPaddingBottom={true}
      type="large"
      background={background && WORKSHOPS_BG_COLORS[background]}
    >
      <ScrollContainer ref={ref}>
        <Box
          $pt={[40, 120]}
          $px={[20, null]}
          $styles={{
            maxWidth: [null, 1120],
            margin: 'auto',
            position: 'relative',
          }}
        >
          <Row>
            <Col>
              {headline?.text && getHeadline(headline, background)}
              {button && (
                <ButtonAdapter
                  $styles={{marginTop: contextualSpaces.buttonSpaces.afterText.common}}
                  class="hero-button"
                  {...button}
                  type={!background || background == 'workshops-color-grey' ? 'blue' : 'white'}
                />
              )}
            </Col>
            <Col $isSecond={true}>
              {content && <Content {...content} color={textColor || content.color}></Content>}
            </Col>
          </Row>
        </Box>
        <ImagesSection>
          <ImagesContainer $setOffsetTop={!!(!shape?.desktop?.src && image?.desktop?.src)}>
            {shape && (
              <Shape>
                <LazyImageAdapter
                  assets={{
                    desktop: shape?.desktop?.src
                      ? {
                          ...DEFAULT_SHAPE_SIZES.desktop,
                          ...shape.desktop,
                        }
                      : undefined,
                    mobile: shape?.mobile?.src
                      ? {
                          ...DEFAULT_SHAPE_SIZES.mobile,
                          ...shape.mobile,
                        }
                      : undefined,
                  }}
                ></LazyImageAdapter>
              </Shape>
            )}
            {image && (
              <ImageContainer>
                <LazyImageAdapter assets={image}></LazyImageAdapter>
              </ImageContainer>
            )}
            {video && (
              <VideoContainer>
                <Video id={video.id} thumbnail={video.thumbnail}></Video>
              </VideoContainer>
            )}
          </ImagesContainer>
        </ImagesSection>
      </ScrollContainer>
    </Section>
  )
}

export default HeroFirstLevel
